import axios, { type AxiosRequestConfig } from "axios";

import env from "src/config/env";
import type { ApiPayload } from "src/types/api";
import type { ReconciliationStats } from "src/types/reconciliation";
import type { AuthUser } from "src/types/auth";
import type { OrganizationType } from "src/types/settings";
import { onReqFulfilled, onReqRejected, onResFulfilled, onResRejected } from "./helpers";

const instance = axios.create({
	baseURL: env.REACT_APP_API_BASE_URL,
	headers: { "Content-Type": "application/json" },
});

instance.interceptors.request.use(onReqFulfilled, onReqRejected);
instance.interceptors.response.use(onResFulfilled, onResRejected);

/**
 * Typed facade over the axios instance.
 *
 * `onResFulfilled` unwraps every response to `response.data`, so calls resolve to the
 * payload envelope — not the `AxiosResponse` that axios's own types describe. These
 * wrappers re-describe each verb to match what actually comes back, which is why
 * endpoints below can declare real return types instead of being cast at each call site.
 */
const api = {
	get: <T = unknown>(url: string, config?: AxiosRequestConfig) => instance.get(url, config) as unknown as Promise<ApiPayload<T>>,

	post: <T = unknown>(url: string, data?: unknown, config?: AxiosRequestConfig) =>
		instance.post(url, data, config) as unknown as Promise<ApiPayload<T>>,

	put: <T = unknown>(url: string, data?: unknown, config?: AxiosRequestConfig) =>
		instance.put(url, data, config) as unknown as Promise<ApiPayload<T>>,

	patch: <T = unknown>(url: string, data?: unknown, config?: AxiosRequestConfig) =>
		instance.patch(url, data, config) as unknown as Promise<ApiPayload<T>>,

	delete: <T = unknown>(url: string, config?: AxiosRequestConfig) => instance.delete(url, config) as unknown as Promise<ApiPayload<T>>,

	/** For endpoints that return a raw body rather than the JSON envelope (file downloads). */
	getRaw: <T>(url: string, config?: AxiosRequestConfig) => instance.get(url, config) as unknown as Promise<T>,
};

/**
 * Any JSON-serialisable request body. Endpoints never inspect the shape, and `object`
 * accepts an interface — `Record<string, unknown>` does not, because interfaces have no
 * index signature, which would force every form's values type to be widened at the call site.
 */
export type RequestBody = object;

/** Multipart config for the endpoints that accept file uploads. */
export const multipart: AxiosRequestConfig = { headers: { "Content-Type": "multipart/form-data" } };

export const validateToken = () => api.post<{ user?: AuthUser }>("/auth/validate_token");

export const dashboard = <T = unknown>() => api.get<T>("/users/dashboard");

export const logoutFn = () => api.post("/auth/logout");

export const changeDefaultPassword = (data: RequestBody) => api.put("/auth/update-default-password", data);

/** Filters accepted by `GET /reconciliation`. */
export interface ReconciliationQuery {
	entity?: string;
	product?: string;
	start_date?: string | null;
	end_date?: string | null;
}

export const getReconciliation = (params?: ReconciliationQuery) => api.get<ReconciliationStats>("/reconciliation", { params });

export const reconciliationExport = () => api.getRaw<Blob>("/reconciliation/export", { responseType: "blob" });

export const getBusinessOrganizationTypes = () => api.get<OrganizationType[]>("/organization-types");

export default api;
